import React, { useState, useEffect } from 'react'
import { getFacebookProfiles, FacebookProfile } from './core/_requests'
import { IsraelCitySelector } from './IsraelCitySelector'
import { toCdnUrl } from '../../utils/cdnUrl'

interface CreateFromPostModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
  postData: {
    post_id?: string
    title?: string
    content?: string
    media_urls?: string[]
    campaign_id?: string
    campaign_name?: string
    profile_id?: string
  } | null
}

interface AISuggestions {
  listing_type: 'item' | 'vehicle' | 'rental' | 'home_sale'
  suggested_category: string
  suggested_category_id?: string
  title: string
  price: number
  description: string
  confidence: number
  // Vehicle fields
  vehicle_year?: number
  vehicle_mileage?: number
  vehicle_make?: string
  vehicle_model?: string
  vehicle_fuel_type?: string
  vehicle_transmission?: string
  vehicle_condition?: string
  // Location
  city?: string
  latitude?: number
  longitude?: number
}

export function CreateFromPostModal({
  show,
  onClose,
  onSuccess,
  postData
}: CreateFromPostModalProps) {
  const [profiles, setProfiles] = useState<FacebookProfile[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState('')
  const [loadingProfiles, setLoadingProfiles] = useState(true)

  const [listingType, setListingType] = useState<'item' | 'vehicle' | 'rental' | 'home_sale'>('item')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('0')
  const [condition, setCondition] = useState('used_good')
  const [itemBrand, setItemBrand] = useState('')
  const [itemColor, setItemColor] = useState('')
  const [itemSize, setItemSize] = useState('')
  // Vehicle fields
  const [vehicleCategory, setVehicleCategory] = useState('car&truck/')
  const [vehicleMake, setVehicleMake] = useState('')
  const [vehicleModel, setVehicleModel] = useState('')
  const [vehicleYear, setVehicleYear] = useState('')
  const [vehicleTrim, setVehicleTrim] = useState('')
  const [vehicleMileage, setVehicleMileage] = useState('')
  const [vehicleFuelType, setVehicleFuelType] = useState('')
  const [vehicleType, setVehicleType] = useState('')
  const [vehicleCondition, setVehicleCondition] = useState('')
  const [vehicleTransmission, setVehicleTransmission] = useState('automatic')
  const [vehicleExteriorColor, setVehicleExteriorColor] = useState('')
  const [vehicleInteriorColor, setVehicleInteriorColor] = useState('')
  const [location, setLocation] = useState<{ lat: number; lng: number; city: string }>({ lat: 0, lng: 0, city: '' })
  const [photos, setPhotos] = useState<string[]>([])
  const [categoryId, setCategoryId] = useState('1670493229902393') // Default: General

  const [autoReplyMessage, setAutoReplyMessage] = useState('')

  const [analyzing, setAnalyzing] = useState(false)
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestions | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Facebook Marketplace category options
  const categoryOptions: Record<string, {id: string, name: string}[]> = {
    item: [
      { id: '1567543000236608', name: 'תיקים ומזוודות' },
      { id: '1583634935226685', name: 'ריהוט' },
      { id: '678754142233400', name: 'מכשירי חשמל למטבח' },
      { id: '686977074745292', name: 'משחקי וידאו' },
      { id: '214968118845643', name: 'תכשיטים' },
      { id: '1266429133383966', name: 'תיקים ואביזרים' },
      { id: '1670493229902393', name: 'כללי' },
      { id: '1569171756675761', name: 'לבית' },
      { id: '613858625416355', name: 'ספרים/סרטים/מוזיקה' },
    ],
    vehicle: [{ id: '807311116002614', name: 'מכוניות' }],
    rental: [{ id: '1468271819871448', name: 'נכס להשכרה' }],
    home_sale: [{ id: '821056594720130', name: 'נכס למכירה' }]
  }

  // Load profiles on mount
  useEffect(() => {
    if (show) {
      loadProfiles()
    }
  }, [show])

  // Populate form with post data when provided
  useEffect(() => {
    if (show && postData) {
      // Set initial values from post
      if (postData.title) setTitle(postData.title)
      if (postData.content) setDescription(postData.content)
      if (postData.media_urls) setPhotos(postData.media_urls)

      // Trigger AI analysis
      analyzePost()
    }
  }, [show, postData])

  // Reset category when listing type changes
  useEffect(() => {
    setCategoryId(categoryOptions[listingType][0]?.id || '')
  }, [listingType])

  const loadProfiles = async () => {
    setLoadingProfiles(true)
    try {
      const response = await getFacebookProfiles()
      setProfiles(response.profiles || [])

      // Pre-select the profile from postData if available, otherwise use first profile
      if (postData?.profile_id) {
        // Check if this profile exists in the list
        const matchingProfile = response.profiles?.find(p => p.id === postData.profile_id)
        if (matchingProfile) {
          setSelectedProfileId(matchingProfile.id)
        } else if (response.profiles && response.profiles.length > 0) {
          setSelectedProfileId(response.profiles[0].id)
        }
      } else if (response.profiles && response.profiles.length > 0) {
        setSelectedProfileId(response.profiles[0].id)
      }
    } catch (err) {
      console.error('Failed to load profiles:', err)
    } finally {
      setLoadingProfiles(false)
    }
  }

  const analyzePost = async () => {
    console.log('[CreateFromPostModal] analyzePost called with postData:', postData)
    if (!postData?.content && !postData?.title) {
      console.log('[CreateFromPostModal] No content or title, skipping analysis')
      return
    }

    setAnalyzing(true)
    console.log('[CreateFromPostModal] Calling analyze-post API...')
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/analyze-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify({
          post_content: postData.content || postData.title,
          image_urls: postData.media_urls || []
        })
      })

      console.log('[CreateFromPostModal] API response status:', response.status)
      if (response.ok) {
        const suggestions = await response.json()
        console.log('[CreateFromPostModal] AI suggestions:', suggestions)
        setAiSuggestions(suggestions)

        // Apply AI suggestions
        if (suggestions.listing_type) setListingType(suggestions.listing_type)
        if (suggestions.title) setTitle(suggestions.title)
        if (suggestions.description) setDescription(suggestions.description)
        setPrice(suggestions.price > 0 ? suggestions.price.toString() : '0')
        if (suggestions.suggested_category_id) setCategoryId(suggestions.suggested_category_id)
        // Apply vehicle fields
        if (suggestions.vehicle_year) setVehicleYear(suggestions.vehicle_year.toString())
        if (suggestions.vehicle_mileage) setVehicleMileage(suggestions.vehicle_mileage.toString())
        if (suggestions.vehicle_make) setVehicleMake(suggestions.vehicle_make)
        if (suggestions.vehicle_model) setVehicleModel(suggestions.vehicle_model)
        if (suggestions.vehicle_fuel_type) setVehicleFuelType(suggestions.vehicle_fuel_type)
        if (suggestions.vehicle_transmission) setVehicleTransmission(suggestions.vehicle_transmission)
        if (suggestions.vehicle_condition) setVehicleCondition(suggestions.vehicle_condition)
        // Apply location from AI
        if (suggestions.city && suggestions.latitude && suggestions.longitude) {
          setLocation({ lat: suggestions.latitude, lng: suggestions.longitude, city: suggestions.city })
        }
      } else {
        console.error('[CreateFromPostModal] API error:', await response.text())
      }
    } catch (err) {
      console.error('[CreateFromPostModal] AI analysis failed:', err)
      // Continue without AI suggestions - user can fill manually
    } finally {
      setAnalyzing(false)
    }
  }

  const scrollToField = (fieldId: string, errorMsg: string) => {
    setError(errorMsg)
    const el = document.getElementById(fieldId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.style.transition = 'box-shadow 0.3s'
      el.style.boxShadow = '0 0 0 2px #dc3545'
      setTimeout(() => { el.style.boxShadow = '' }, 3000)
    }
  }

  const handleSubmit = async () => {
    // Validation with scroll-to-error
    if (!selectedProfileId) {
      scrollToField('cfp-field-profile', 'יש לבחור פרופיל')
      return
    }
    if (!title.trim()) {
      scrollToField('cfp-field-title', 'יש להזין כותרת')
      return
    }
    if (!price || parseFloat(price) <= 0) {
      scrollToField('cfp-field-price', 'יש להזין מחיר תקין')
      return
    }
    if (!location.city) {
      scrollToField('cfp-field-location', 'יש לבחור מיקום')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/marketplace/synced-listings/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}').api_token}`
        },
        body: JSON.stringify({
          profile_id: selectedProfileId,
          listing_type: listingType,
          title,
          description,
          price: parseFloat(price),
          condition: listingType === 'item' ? condition : undefined,
          vehicle_data: listingType === 'vehicle' ? {
            vehicleCategory,
            make: vehicleMake,
            model: vehicleModel,
            year: vehicleYear ? parseInt(vehicleYear) : undefined,
            trim: vehicleTrim || undefined,
            mileage: vehicleMileage || undefined,
            fuelType: vehicleFuelType || undefined,
            vehicleType: vehicleType || undefined,
            vehicleCondition: vehicleCondition || undefined,
            isManualTransmission: vehicleTransmission === 'manual',
            exteriorColor: vehicleExteriorColor || undefined,
            interiorColor: vehicleInteriorColor || undefined,
          } : undefined,
          category_id: categoryId,
          latitude: location.lat,
          longitude: location.lng,
          city: location.city,
          photo_urls: photos,
          source_post_id: postData?.post_id,
          source_campaign_id: postData?.campaign_id,
          auto_reply_message: autoReplyMessage.trim() || undefined
        })
      })

      const result = await response.json()

      if (result.success) {
        onSuccess()
        onClose()
      } else {
        setError(result.error || 'שגיאה ביצירת המודעה')
      }
    } catch (err) {
      console.error('Failed to create listing:', err)
      setError('שגיאה ביצירת המודעה')
    } finally {
      setLoading(false)
    }
  }

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index))
  }

  if (!show) return null

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-shop me-2"></i>
              פרסום במרקטפלייס מפוסט
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* AI Analysis Status */}
            {analyzing && (
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <span className="spinner-border spinner-border-sm me-3"></span>
                  <div>
                    <strong>מנתח את הפוסט עם AI...</strong>
                    <div className="small text-muted">מזהה סוג מודעה, מחיר, כותרת ותיאור</div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Detection Results */}
            {aiSuggestions && !analyzing && (
              <div className={`alert ${aiSuggestions.confidence > 0.5 ? 'alert-success' : 'alert-warning'} mb-4`}>
                <div className="d-flex align-items-start">
                  <i className={`bi ${aiSuggestions.confidence > 0.5 ? 'bi-magic' : 'bi-exclamation-triangle'} fs-4 me-3`}></i>
                  <div className="flex-grow-1">
                    <strong>
                      {aiSuggestions.confidence > 0.5
                        ? 'AI זיהה את הפרטים הבאים:'
                        : 'AI לא הצליח לזהות את כל הפרטים - נא למלא ידנית'}
                    </strong>
                    <div className="mt-2 small">
                      <div className="row">
                        <div className="col-6 mb-1">
                          <span className="text-muted">סוג:</span>{' '}
                          <strong>
                            {aiSuggestions.listing_type === 'item' && 'פריט'}
                            {aiSuggestions.listing_type === 'vehicle' && 'רכב'}
                            {aiSuggestions.listing_type === 'rental' && 'השכרה'}
                            {aiSuggestions.listing_type === 'home_sale' && 'מכירת נכס'}
                          </strong>
                        </div>
                        <div className="col-6 mb-1">
                          <span className="text-muted">מחיר:</span>{' '}
                          <strong>{aiSuggestions.price > 0 ? `₪${aiSuggestions.price.toLocaleString()}` : 'לא זוהה'}</strong>
                        </div>
                        <div className="col-6 mb-1">
                          <span className="text-muted">קטגוריה:</span>{' '}
                          <strong>{aiSuggestions.suggested_category}</strong>
                        </div>
                        <div className="col-6 mb-1">
                          <span className="text-muted">ביטחון:</span>{' '}
                          <strong>{Math.round(aiSuggestions.confidence * 100)}%</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger mb-4">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Profile Selection */}
            <div className="mb-4" id="cfp-field-profile">
              <label className="form-label fw-bold">פרופיל לפרסום</label>
              {loadingProfiles ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  טוען פרופילים...
                </div>
              ) : (
                <select
                  className="form-select"
                  value={selectedProfileId}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                >
                  <option value="">בחר פרופיל</option>
                  {profiles.map(profile => (
                    <option key={profile.id} value={profile.id}>{profile.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Listing Type */}
            <div className="mb-4">
              <label className="form-label fw-bold">סוג מודעה</label>
              <div className="d-flex gap-2 flex-wrap">
                {[
                  { value: 'item', label: 'פריט', icon: 'bi-box' },
                  { value: 'vehicle', label: 'רכב', icon: 'bi-car-front' },
                  { value: 'rental', label: 'השכרה', icon: 'bi-house' },
                  { value: 'home_sale', label: 'מכירת נכס', icon: 'bi-building' }
                ].map(type => (
                  <button
                    key={type.value}
                    type="button"
                    className={`btn ${listingType === type.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setListingType(type.value as any)}
                  >
                    <i className={`${type.icon} me-2`}></i>
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Category - only show selector for items, others have a single fixed category */}
            {listingType === 'item' ? (
              <div className="mb-4">
                <label className="form-label fw-bold">קטגוריה</label>
                <select
                  className="form-select"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  {categoryOptions[listingType].map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            ) : null}

            {/* Title */}
            <div className="mb-4" id="cfp-field-title">
              <label className="form-label fw-bold">כותרת</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="כותרת המודעה"
                maxLength={100}
              />
              <small className="text-muted">{title.length}/100</small>
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="form-label fw-bold">תיאור</label>
              <textarea
                className="form-control"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="תיאור הפריט"
                rows={4}
              />
            </div>

            {/* Price */}
            <div className="mb-4" id="cfp-field-price">
              <label className="form-label fw-bold">מחיר (₪)</label>
              <input
                type="number"
                className="form-control"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                min="0"
              />
            </div>

            {/* Item fields */}
            {listingType === 'item' && (
              <>
                <div className="mb-4">
                  <label className="form-label fw-bold">מצב הפריט</label>
                  <select
                    className="form-select"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                  >
                    <option value="new">חדש</option>
                    <option value="used_like_new">כמו חדש</option>
                    <option value="used_good">מצב טוב</option>
                    <option value="used_fair">מצב סביר</option>
                  </select>
                </div>
                <div className="row mb-4">
                  <div className="col-6">
                    <label className="form-label fw-bold">מותג</label>
                    <input type="text" className="form-control" value={itemBrand} onChange={(e) => setItemBrand(e.target.value)} placeholder="לדוגמה: Apple, Nike" />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold">צבע</label>
                    <input type="text" className="form-control" value={itemColor} onChange={(e) => setItemColor(e.target.value)} placeholder="לדוגמה: שחור, לבן" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="form-label fw-bold">מידה</label>
                  <input type="text" className="form-control" value={itemSize} onChange={(e) => setItemSize(e.target.value)} placeholder="לדוגמה: M, 42" />
                </div>
              </>
            )}

            {/* Vehicle fields */}
            {listingType === 'vehicle' && (
              <>
                <h6 className="fw-bold mt-3 mb-3">פרטי הרכב</h6>
                <div className="mb-3">
                  <label className="form-label fw-bold">קטגוריית רכב</label>
                  <select className="form-select" value={vehicleCategory} onChange={(e) => setVehicleCategory(e.target.value)}>
                    <option value="car&truck/">רכב / משאית</option>
                    <option value="motorcycle/">אופנוע</option>
                    <option value="powersport/">כלי ספורט ממונע</option>
                    <option value="rv&camper/">קרוואן / קמפר</option>
                    <option value="boat/">סירה</option>
                    <option value="commercial&industrial/">מסחרי / תעשייתי</option>
                    <option value="trailer/">נגרר</option>
                  </select>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label fw-bold">יצרן *</label>
                    <input type="text" className="form-control" value={vehicleMake} onChange={(e) => setVehicleMake(e.target.value)} placeholder="לדוגמה: טסלה, טויוטה" />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold">דגם *</label>
                    <input type="text" className="form-control" value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)} placeholder="לדוגמה: Model 3, Camry" />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label fw-bold">שנה *</label>
                    <input type="number" className="form-control" value={vehicleYear} onChange={(e) => setVehicleYear(e.target.value)} placeholder="לדוגמה: 2024" min={1900} max={2030} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold">גרסה / Trim</label>
                    <input type="text" className="form-control" value={vehicleTrim} onChange={(e) => setVehicleTrim(e.target.value)} placeholder="לדוגמה: Sport, Limited" />
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label fw-bold">קילומטראז׳</label>
                    <input type="text" className="form-control" value={vehicleMileage} onChange={(e) => setVehicleMileage(e.target.value)} placeholder="לדוגמה: 50000" />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold">סוג דלק</label>
                    <select className="form-select" value={vehicleFuelType} onChange={(e) => setVehicleFuelType(e.target.value)}>
                      <option value="">בחר סוג דלק</option>
                      <option value="GASOLINE">בנזין</option>
                      <option value="DIESEL">דיזל</option>
                      <option value="ELECTRIC">חשמלי</option>
                      <option value="HYBRID">היברידי</option>
                      <option value="FLEX">פלקס</option>
                      <option value="OTHER">אחר</option>
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label fw-bold">סוג רכב</label>
                    <select className="form-select" value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
                      <option value="">בחר סוג</option>
                      <option value="sedan">סדאן</option>
                      <option value="suv">SUV</option>
                      <option value="truck">טנדר / משאית</option>
                      <option value="coupe">קופה</option>
                      <option value="convertible">קבריולט</option>
                      <option value="hatchback">האצ׳בק</option>
                      <option value="wagon">סטיישן</option>
                      <option value="van">ואן</option>
                      <option value="minivan">מיניוואן</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold">מצב הרכב</label>
                    <select className="form-select" value={vehicleCondition} onChange={(e) => setVehicleCondition(e.target.value)}>
                      <option value="">בחר מצב</option>
                      <option value="excellent">מצוין</option>
                      <option value="very_good">טוב מאוד</option>
                      <option value="good">טוב</option>
                      <option value="fair">סביר</option>
                      <option value="poor">גרוע</option>
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label fw-bold">תיבת הילוכים</label>
                    <select className="form-select" value={vehicleTransmission} onChange={(e) => setVehicleTransmission(e.target.value)}>
                      <option value="automatic">אוטומטית</option>
                      <option value="manual">ידנית</option>
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label fw-bold">צבע חיצוני</label>
                    <input type="text" className="form-control" value={vehicleExteriorColor} onChange={(e) => setVehicleExteriorColor(e.target.value)} placeholder="לדוגמה: שחור, לבן" />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold">צבע פנימי</label>
                    <input type="text" className="form-control" value={vehicleInteriorColor} onChange={(e) => setVehicleInteriorColor(e.target.value)} placeholder="לדוגמה: שחור, בז׳" />
                  </div>
                </div>
              </>
            )}

            {/* Location */}
            <div className="mb-4" id="cfp-field-location">
              <label className="form-label fw-bold">מיקום</label>
              <IsraelCitySelector
                value={location}
                onChange={setLocation}
              />
            </div>

            {/* Auto-Reply Message */}
            <div className="mb-4">
              <label className="form-label fw-bold">הודעת תגובה אוטומטית</label>
              <textarea
                className="form-control"
                value={autoReplyMessage}
                onChange={(e) => setAutoReplyMessage(e.target.value)}
                placeholder="הודעה שתישלח אוטומטית למתעניינים..."
                rows={2}
              />
              <small className="text-muted">אופציונלי - תישלח אוטומטית לכל מי שכותב על המודעה</small>
            </div>

            {/* Photos */}
            <div className="mb-4">
              <label className="form-label fw-bold">תמונות</label>
              {photos.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {photos.map((url, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img
                        src={toCdnUrl(url)}
                        alt={`Photo ${idx + 1}`}
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        style={{ position: 'absolute', top: '-5px', right: '-5px', padding: '2px 6px', fontSize: '10px' }}
                        onClick={() => removePhoto(idx)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted py-3 text-center border rounded">
                  אין תמונות מהפוסט
                </div>
              )}
            </div>

            {/* Source info */}
            {postData?.campaign_name && (
              <div className="text-muted small">
                <i className="bi bi-folder me-1"></i>
                מקור: {postData.campaign_name}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-light" onClick={onClose}>
              ביטול
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  יוצר מודעה...
                </>
              ) : (
                <>
                  <i className="bi bi-shop me-2"></i>
                  פרסם במרקטפלייס
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateFromPostModal
